/**
 * Audio output sink — RAM-resident until the output gets big, then OPFS.
 *
 * Phase D of the streaming pipeline (2026-05-08). Closes the last O(file)
 * memory leak in the streaming chain: even with chunked encode (Phase A)
 * + chunked decode (Phase B), the GROWING ARRAY of encoded mp3 chunks
 * sits in main-thread RAM until finish() assembles the Blob. For a
 * 5-hour podcast at 128 kbps that's ~280 MB. Phase D writes those bytes
 * progressively to the Origin Private File System (OPFS) — disk-backed,
 * sandboxed inside the origin, no permission prompt, transparent cleanup.
 *
 * After Phase D + streaming encoder/decoder the entire pipeline is
 * O(chunk_size) memory (~5 KB) regardless of source duration.
 *
 * OPFS support (2026): Chrome 102+, Edge 102+, Firefox 111+, Safari 15.2+.
 * Practically every browser since 2023. We fall back to the in-memory
 * accumulator on the rare unsupported case — and, since 2026-08-11, we
 * START there and only move to disk once the output passes SPILL_AT. A
 * disk tier that a 1 MB export never touches cannot fail a 1 MB export;
 * see the SPILL_AT comment for the measurement that motivated it.
 *
 * Usage:
 *
 *     const sink = await OutputSink.create({ mime: 'audio/mpeg', name: 'volume-out' });
 *     await sink.write(chunk1);     // Uint8Array | ArrayBuffer
 *     await sink.write(chunk2);
 *     const blob = await sink.finish();   // Blob — backed by OPFS or RAM
 *     await sink.cleanup();               // delete OPFS file (no-op for RAM)
 *
 * Performance: OPFS writes use FileSystemWritableFileStream (async). On
 * modern browsers each write is sub-millisecond (kernel page cache buffers
 * — actual disk I/O is lazy). For a 10-minute podcast (~10 MB output)
 * total OPFS write overhead is < 50 ms on commodity hardware.
 */
(function () {
    'use strict';

    function isOpfsAvailable() {
        return !!(navigator && navigator.storage && typeof navigator.storage.getDirectory === 'function');
    }

    // ── Who removes a sink file, and when ────────────────────────────────────
    // Not this module, and not by age. Until 2026-10-14 every create() swept
    // `_audio_sink_*` files whose NAME was stamped more than an hour ago. The
    // stamp is the moment the file was created, and «an hour old» cannot tell
    // a leftover from a result a living page still holds — the Blob handed out
    // is a VIEW of the file. Measured (tests/playwright/
    // output-sink-file-outlives-the-hour.mjs, Chromium and Firefox): a result
    // created two hours earlier and still held by its page was destroyed by the
    // next export in that page or in any other tab of the site; the Blob kept
    // reporting its size and reading it threw. That is a batch that runs past
    // the hour and is delivered as one archive at the end, parts of a batch
    // waiting on the «Save» bar, a result the person comes back to after lunch.
    //
    // The file now carries its OWNER (window.__opfsOwner, timbrica-core.js):
    // the site's one janitor leaves a living page's files alone and removes a
    // dead page's on the next page load. Inside a living page a file goes when
    // the tool says it is done with it — release(blob) — or with the page.
    function sinkFileName(label) {
        var tag = '';
        try { tag = (window.__opfsOwner && window.__opfsOwner.tag('_')) || ''; } catch (_) {}
        // Stamp and owner FIRST: the janitor reads both from the head of the
        // name, so a tool-chosen label can never be taken for an owner tag
        // (labels are also reduced to letters, digits and dots for that reason).
        // The random part keeps two sinks spilling in the same millisecond apart.
        return '_audio_sink_' + Date.now() + tag + '_' + Math.random().toString(36).slice(2, 6)
            + '.' + String(label).replace(/[^A-Za-z0-9]+/g, '.').slice(0, 60) + '.bin';
    }
    // Blob handed out → the file behind it, for release().
    var backing = (typeof WeakMap === 'function') ? new WeakMap() : null;
    function remember(blob, rec) { try { if (backing && blob && rec) backing.set(blob, rec); } catch (_) {} return blob; }
    /**
     * The tool is done with a result it got from a sink: give the disk back
     * now. Only for a Blob nothing reads any more — a preview player, a pending
     * download or an archive built over it would lose its bytes.
     * → Promise<boolean> (false: not a disk-backed sink result, or already gone).
     */
    function release(blob) {
        var rec = null;
        try { rec = backing && blob ? backing.get(blob) : null; } catch (_) {}
        if (!rec || rec.gone) return Promise.resolve(false);
        rec.gone = true;
        return Promise.resolve(rec.dir.removeEntry(rec.fileName)).then(function () {
            try { if (typeof window.__ae === 'function') window.__ae('feature_use', { f: 'output_sink_released', mb: Math.round((rec.bytes || 0) / 1048576) }); } catch (_) {}
            return true;
        }, function () { return false; });
    }

    // Below this the whole output is kept in the JS heap and OPFS is never
    // touched. Rationale, measured 2026-08-11 over 7 days of
    // `audio_stream_pipeline_ok`: the output is ≤ 5 MB on the overwhelming
    // majority of runs, so for those the disk tier bought nothing and cost a
    // failure surface — 50 runs / 14 d already died on OPFS with a thrown
    // NotFoundError / NotReadableError / QuotaExceededError, and hangs inside
    // it are invisible by construction (bug report 2026-08-11, /ru/volume,
    // 933 KB WAV: six exports, six endless spinners). 32 MB is well under the RAM
    // the caller already holds — /volume decodes the entire file into an
    // AudioBuffer to draw the waveform before any of this runs — and well
    // under the unbounded heap the pre-existing RAM fallback used whenever
    // OPFS was merely *absent*. Above it we spill and the OOM protection the
    // disk tier exists for is unchanged.
    var SPILL_AT = 32 * 1024 * 1024;

    /**
     * Create a new sink. Caller writes bytes via .write(), then .finish()
     * to get the final Blob. .cleanup() removes the OPFS file (or no-op
     * while the sink is still RAM-resident).
     *
     * The sink starts in RAM and moves itself to OPFS the moment the output
     * grows past `spillAt` — so the disk tier is paid for only by the outputs
     * that actually need it. `mode` reports where the bytes live RIGHT NOW
     * ('ram' → 'opfs' after a spill); callers use it for telemetry only.
     *
     * @param {object} opts - { mime: 'audio/mpeg', name?: string, spillAt?: number }
     * @returns {Promise<{write, finish, cleanup, mode, bytesWritten}>}
     */
    async function create(opts) {
        opts = opts || {};
        var mime = opts.mime || 'application/octet-stream';
        var name = opts.name || ('audio-output-' + Date.now() + '-' + Math.floor(Math.random() * 1e6));
        // `__OUTPUT_SINK_SPILL_AT` lets a test drive the disk tier on a small
        // fixture, the same seam the pipeline's `__PIPELINE_*_OVERRIDE` knobs
        // provide — without it the OPFS path is only reachable by generating
        // a 32 MB export in the browser.
        var spillAt = (typeof opts.spillAt === 'number' && opts.spillAt >= 0) ? opts.spillAt
            : (typeof window !== 'undefined' && typeof window.__OUTPUT_SINK_SPILL_AT === 'number') ? window.__OUTPUT_SINK_SPILL_AT
            : SPILL_AT;

        var collected = [];        // RAM tier — chunks not yet on disk
        var bytesWritten = 0;
        var closed = false;
        var finished = false;
        var dir = null, fh = null, ws = null, fileName = null;

        var sink = {
            mode: 'ram',
            bytesWritten: function () { return bytesWritten; },

            write: async function (chunk) {
                if (closed) throw new Error('OutputSink: write after finish/cleanup');
                if (!chunk) return;
                var view;
                if (chunk instanceof ArrayBuffer) {
                    view = new Uint8Array(chunk);
                } else if (ArrayBuffer.isView(chunk)) {
                    view = chunk;
                } else {
                    throw new Error('OutputSink.write: expected ArrayBuffer or typed array');
                }
                if (view.byteLength === 0) return;
                if (ws) {
                    await ws.write(view);
                    bytesWritten += view.byteLength;
                    return;
                }
                // Always copy: the caller may have transferred the underlying
                // ArrayBuffer through postMessage and we cannot rely on it
                // being valid later. Cheap on small chunks (typical 5-50 KB).
                collected.push(new Uint8Array(view));
                bytesWritten += view.byteLength;
                if (bytesWritten > spillAt) await spillToDisk();
            },

            finish: async function () {
                if (closed) throw new Error('OutputSink: finish called twice');
                closed = true;
                if (!ws) {
                    // Never grew past the threshold — the bytes are already a
                    // contiguous set of buffers in this process. No disk, so
                    // none of the disk failure modes below can apply.
                    var ramBlob = new Blob(collected, { type: mime });
                    collected = null;
                    finished = true;
                    return ramBlob;
                }
                await ws.close();
                var file = await fh.getFile();
                // ⚠️ A sink that returns FEWER bytes than it was given is
                // broken, and staying silent about it is worse than
                // failing: the caller ships the empty Blob to the
                // download primitive and the user gets a 0-byte file
                // with a cheerful "saved!" — 42 such runs in 30 days,
                // every one of them WebKit (`empty_download blob.size=0`,
                // e.g. `lite_chunked_done windows:47 outMB:0`).
                // Some engines hand back a STALE File snapshot right
                // after close(); re-opening the handle re-reads it. If
                // the size still disagrees the bytes are genuinely gone,
                // so throw a typed error and let the caller redo the work
                // in RAM rather than deliver nothing.
                if (file.size !== bytesWritten) {
                    try {
                        var freshFh = await dir.getFileHandle(fileName);
                        file = await freshFh.getFile();
                    } catch (_) { /* keep the first snapshot */ }
                }
                if (file.size !== bytesWritten) {
                    try {
                        if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                            window.__ae('feature_use', {
                                f: 'output_sink_short_read',
                                wrote: bytesWritten,
                                got: file.size,
                            });
                        }
                    } catch (_) {}
                    var shortErr = new Error('OutputSink: disk returned ' + file.size
                        + ' bytes for ' + bytesWritten + ' written');
                    shortErr.code = 'SINK_SHORT_READ';
                    shortErr.bytesWritten = bytesWritten;
                    throw shortErr;
                }
                // Only NOW is the file handed out — so only now does
                // cleanup() have to spare it (see the contract below).
                // A finish() that threw above leaves finished=false, so
                // the caller's cleanup() reclaims the dead file at once.
                finished = true;
                // file IS-A Blob in browsers — return it directly. This
                // is a VIEW over the OPFS file, not a copy: the bytes
                // are read lazily when someone consumes the Blob.
                return remember(new Blob([file], { type: mime }), { dir: dir, fileName: fileName, bytes: bytesWritten });
            },

            // ⚠️ CONTRACT: after finish(), cleanup() must NOT delete the
            // file. The Blob handed to the caller is a lazy view over
            // it, so deleting makes that Blob silently unreadable —
            // `blob.size` still reports the full length while reading
            // throws NotFoundError. MEASURED: an 8 MB sink read fine
            // before cleanup() and threw NotFoundError right after.
            // Downstream that surfaces as a download of 0 bytes while
            // our telemetry happily reports "delivered N MB" — exactly
            // the «join-5tracks.mp3 — 0 Б, Ошибка загрузки» a Premium
            // user hit on 2026-07-27 (an Android browser reads the blob
            // out-of-process, so it loses the race a desktop wins).
            // The space comes back when the tool releases the Blob
            // (OutputSink.release) or when the page is gone (the janitor,
            // __opfsOwner). Before finish() (i.e. a cancelled run) nothing
            // was handed out, so deleting immediately is correct and still
            // done. A RAM-resident sink has nothing on disk to spare or delete.
            cleanup: async function () {
                closed = true;
                collected = null;
                if (!ws) return;
                try { await ws.close(); } catch (_) {}
                if (finished) return;   // blob is live — released by the tool, or reclaimed with the page
                try { await dir.removeEntry(fileName); } catch (_) {}
            },
        };

        // Move everything accumulated so far onto disk and keep writing there.
        // Failure to spill is NOT fatal: staying in RAM is exactly what this
        // sink did on every browser without OPFS, so degrading to it is never
        // worse than the behaviour we already shipped — whereas failing the
        // export loses work the user already waited for.
        async function spillToDisk() {
            if (ws || !isOpfsAvailable()) return;
            try {
                dir = await navigator.storage.getDirectory();
                // Unique-per-call filename so concurrent/aborted runs can't
                // collide. NOTE: the Blob returned by finish() is a VIEW over
                // this file — it does NOT copy the bytes (measured: reading it
                // after removeEntry throws NotFoundError). The name carries the
                // page that owns it — see sinkFileName().
                fileName = sinkFileName(name);
                fh = await dir.getFileHandle(fileName, { create: true });
                var stream = await fh.createWritable();
                for (var i = 0; i < collected.length; i++) await stream.write(collected[i]);
                ws = stream;                 // only now is the disk tier live
                collected = null;
                sink.mode = 'opfs';
                try {
                    if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                        window.__ae('feature_use', { f: 'output_sink_spilled', atMB: Math.round(bytesWritten / 1048576) });
                    }
                } catch (_) {}
            } catch (e) {
                ws = null; fh = null; dir = null; fileName = null;
                console.warn('[audio-output-sink] spill to OPFS failed, staying in RAM:', e && e.message);
                try {
                    if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                        window.__ae('feature_use', {
                            f: 'output_sink_spill_failed',
                            atMB: Math.round(bytesWritten / 1048576),
                            err: ((e && e.message) || String(e)).slice(0, 120),
                        });
                    }
                } catch (_) {}
                // Retrying on every subsequent write would re-pay a failing
                // OPFS call thousands of times; one attempt per sink is enough.
                spillAt = Infinity;
            }
        }

        return sink;
    }

    /* __pcm16:begin
     * float → 16 бит. ОДИН квантователь сайта: этот блок одинаков (с точностью
     * до отступа) в audio-pipeline.js, media-pipeline/audio-native.js,
     * audio-post.js, audio-output-sink.js, audio-recover.js и timbrica-core.js
     * (там — window.__pcm16Quantizer для тулов со своим Int16; сторож —
     * tests/js/pcm16-quantize.test.mjs). Замеры и отвергнутые варианты —
     * docs/why/pcm16-quantize.md.
     *
     * pcm16Quantizer(dither) → q(src, n, dst, at, stride): n выборок канала src
     * пишутся в dst[at], dst[at + stride], … (Int16Array, каналы чередуются).
     *  · Масштаб — как у декодеров Chrome и Firefox (минус ×32768, плюс ×32767):
     *    нетронутый 16-битный звук возвращается бит-в-бит. Прежнее отрезание
     *    `| 0` меняло 27–30 % его выборок на единицу к нулю.
     *  · Блок из 1024 выборок, целиком лежащий на 16-битной сетке (нетронутый
     *    звук, цифровая тишина, выход декодеров с делением на 32768), копируется
     *    точно — без шума и без округления мимо.
     *  · dither=true — TPDF ±1 младший бит (готовый файл: звук тише младшего бита
     *    уходит в шум, а не в тишину и не в гармоники).
     *  · dither=false — округление «половина к чётному», без шума: для того, что
     *    ещё будут складывать (стемы) — сумма четырёх стемов на 9,6 дБ тише по шуму.
     *  · Зерно постоянное: один и тот же вход — одни и те же байты.
     */
    function pcm16Quantizer(dither) {
        var x = 0x9E3779B9, TOL = 1 / 256, BLOCK = 1024;
        // Весь блок лежит на сетке ОДНОГО декодера (плюс ×pos: 32767 — Chrome и
        // Firefox, 32768 — ffmpeg)? Тогда это нетронутый звук: пишем точно.
        // Сетка выбирается на блок: по выборке выход ffmpeg у полной шкалы
        // (32640…32767) ложно садился на сетку 32767 и терял единицу.
        function exact(src, b, e, dst, at, stride, pos) {
            for (var i = b, w = at + b * stride; i < e; i++, w += stride) {
                var s = src[i];
                if (s !== s) s = 0;
                var v = s < 0 ? s * 32768 : s * pos, k = Math.floor(v + 0.5), d = v - k;
                if (d > TOL || d < -TOL) return false;
                dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
            }
            return true;
        }
        return function (src, n, dst, at, stride) {
            // Состояние генератора — в локальной на время вызова: через замыкание
            // горячий цикл вдвое-вчетверо медленнее (замер в Chrome 156).
            var b, e, i, w, s, v, k, r = x, tri = dither;
            for (b = 0; b < n; b += BLOCK) {
                e = b + BLOCK < n ? b + BLOCK : n;
                if (exact(src, b, e, dst, at, stride, 32767) || exact(src, b, e, dst, at, stride, 32768)) continue;
                for (i = b, w = at + b * stride; i < e; i++, w += stride) {
                    s = src[i];
                    if (s !== s) s = 0;
                    v = s < 0 ? s * 32768 : s * 32767;
                    if (tri) {
                        r ^= r << 13; r ^= r >>> 17; r ^= r << 5;
                        k = Math.floor(v + ((r & 0xFFFF) + ((r >>> 16) & 0xFFFF)) * 1.52587890625e-5 - 0.5);  // ×2^-16
                    } else {
                        k = Math.floor(v + 0.5);
                        if (k - v === 0.5 && (k & 1)) k--;
                    }
                    dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
                }
            }
            x = r;
        };
    }
    /* __pcm16:end */

    /**
     * Streaming WAV writer over a sink. Writes a placeholder header (wavHeader:
     * 44 bytes up to stereo, 68 above), appends PCM per chunk, and patches the
     * sizes on finalize(). opts.mask — the speaker mask, when the caller's
     * layout is not the default for its channel count.
     *
     * Why it lives HERE (moved from audio-stream-pipeline.js 2026-08-29): it is
     * the output half of every "render audio in windows" lane, it depends on
     * nothing but a sink, and the module that used to own it is 86 KB of
     * mastering DSP. /channel-converter needs the writer and none of that DSP;
     * making it load the whole pipeline to reach 60 lines is how a second copy
     * of those 60 lines gets written instead. This module is already global on
     * every media page, so the writer now costs its consumers nothing.
     *
     * Peak memory is one chunk: `channelArrays` in, interleaved bytes out,
     * straight to the sink. Nothing accumulates in JS.
     *
     *   const sink = await OutputSink.create({ mime:'audio/wav', name:'upmix' });
     *   const w = OutputSink.wavWriter(sink, 44100, 6, 24);
     *   await w.writeChunk([Float32Array, ...]);   // one per channel, equal length
     *   const blob = await w.finalize();           // disk-backed when the sink spilled
     *
     * bitsPerSample: 16 | 24 | 32 (32 = IEEE float). ⚠️ WAV's RIFF sizes are
     * 32-bit, so the data chunk cannot exceed 4 GiB — `wouldOverflow()` says so
     * BEFORE the work, because finding out afterwards means a file that some
     * players read as the wrong length and others refuse.
     */
    var WAV_MAX_DATA_BYTES = 0xFFFFFFFF - 36;

    /* ── WAV header — one contract for every writer of a file (2026-10-09) ──
       Up to stereo: the plain 44-byte header (tag 1 PCM / 3 IEEE float) —
       every reader takes it, byte-identical to what was written before.
       Above stereo: WAVE_FORMAT_EXTENSIBLE (68 bytes) with a SPEAKER MASK. The
       streaming writer used to write the plain header at any channel count:
       /channel-converter's 5.1 WAVs (≈370 in 60 days, almost all of them via
       this writer) said «6 channels» and nothing about which speaker is which —
       ffprobe reads «unknown» and a DAW or an authoring tool has to guess.
       Default masks — the KSAUDIO «surround» set, every one of them opened by
       Chromium 147 and 155 (measured): 3 0x7, 4 0x33 (quad), 5 0x607,
       6 0x60F (5.1), 7 0x70F (6.1), 8 0x63F (7.1), 12 0x2D63F (7.1.4). NOT 0x3F for 5.1:
       Chromium ≤154 refuses it in decodeAudioData. A caller that knows a
       different layout passes its own (/channel-converter's 4 channels are
       3.1 → 0xF; its 7.1.6 has no conformant mask → 0).
       AudioPipeline.audioBufferToWav writes the same bytes (pinned by
       tests/js/wav-header-parity.test.mjs). */
    var WAV_MASK = { 3: 0x7, 4: 0x33, 5: 0x607, 6: 0x60F, 7: 0x70F, 8: 0x63F, 12: 0x2D63F };
    function wavHeader(numChannels, sampleRate, bits, dataBytes, mask) {
        var isFloat = bits === 32, ext = numChannels > 2;
        var fmtLen = ext ? 40 : 16, headLen = 20 + fmtLen + 8;
        var ba = numChannels * (bits / 8), hdr = new Uint8Array(headLen), dv = new DataView(hdr.buffer);
        function ws(o, str) { for (var k = 0; k < str.length; k++) hdr[o + k] = str.charCodeAt(k); }
        ws(0, 'RIFF'); dv.setUint32(4, headLen - 8 + dataBytes, true); ws(8, 'WAVE');
        ws(12, 'fmt '); dv.setUint32(16, fmtLen, true);
        // 3 = IEEE float. Writing 1 (PCM) for 32-bit float data is the classic
        // silent corruption: the file opens, and every sample is read as a
        // gigantic integer. The extensible form carries the tag in SubFormat.
        dv.setUint16(20, ext ? 0xFFFE : (isFloat ? 3 : 1), true);
        dv.setUint16(22, numChannels, true);
        dv.setUint32(24, sampleRate, true); dv.setUint32(28, sampleRate * ba, true);
        dv.setUint16(32, ba, true); dv.setUint16(34, bits, true);
        if (ext) {
            dv.setUint16(36, 22, true);                       // cbSize
            dv.setUint16(38, bits, true);                     // valid bits
            dv.setUint32(40, (typeof mask === 'number' ? mask : WAV_MASK[numChannels]) || 0, true);
            // SubFormat GUID xxxxxxxx-0000-0010-8000-00AA00389B71, first field = format tag.
            hdr.set([isFloat ? 3 : 1, 0, 0, 0, 0, 0, 0x10, 0, 0x80, 0, 0, 0xAA, 0, 0x38, 0x9B, 0x71], 44);
        }
        ws(headLen - 8, 'data'); dv.setUint32(headLen - 4, dataBytes, true);
        return hdr;
    }

    function wavWriter(sink, sampleRate, numChannels, bitsPerSample, opts) {
        var bits = (bitsPerSample === 16 || bitsPerSample === 32) ? bitsPerSample : 24;
        var bytesPerSample = bits / 8;
        var bytesPerFrame = bytesPerSample * numChannels;
        // Sizes patched on finalize; the header is 44 bytes up to stereo, 68 above.
        var hdr = wavHeader(numChannels, sampleRate, bits, 0, opts && opts.mask);
        var headLen = hdr.length;
        // RIFF sizes are 32-bit and count the header after the first 8 bytes.
        var maxData = 0xFFFFFFFF - (headLen - 8);
        var headerWritten = false;
        var dataBytes = 0;
        var scratch = null;
        // 16 бит: один квантователь на весь файл — дизеринг (готовый файл;
        // `opts.dither === false` — округление без шума), нетронутое бит-в-бит.
        var q16 = bits === 16 ? pcm16Quantizer(!(opts && opts.dither === false)) : null;

        return {
            bitsPerSample: bits,
            /** Frames that still fit under WAV's 4 GiB data-chunk ceiling. */
            framesRemaining: function () {
                return Math.floor((maxData - dataBytes) / bytesPerFrame);
            },
            wouldOverflow: function (frames) {
                return (dataBytes + frames * bytesPerFrame) > maxData;
            },
            writeChunk: async function (channelArrays) {
                if (!headerWritten) { await sink.write(hdr); headerWritten = true; }
                var n = channelArrays[0].length;
                var nCh = channelArrays.length;
                var need = n * bytesPerFrame;
                // One scratch buffer, reused: a fresh Uint8Array per window is a
                // multi-MB allocation per window, and it is the garbage — not the
                // live set — that gets a tab killed (measured on /join: 659 MB of
                // transient at 240 windows).
                if (!scratch || scratch.length < need) scratch = new Uint8Array(need);
                var out = scratch.subarray(0, need);
                var odv = new DataView(out.buffer, out.byteOffset, need);
                if (bits === 16) {
                    // Квантователь пишет каналы прямо в чередование. Прежде здесь
                    // был свой TPDF на Math.random: он шумел и в цифровой тишине,
                    // и в нетронутом звуке (бит-в-бит выходило 73 % выборок).
                    var i16 = new Int16Array(out.buffer, out.byteOffset, need >> 1);
                    for (var c16 = 0; c16 < nCh; c16++) q16(channelArrays[c16], n, i16, c16, nCh);
                } else {
                    for (var i = 0; i < n; i++) {
                        for (var ch = 0; ch < nCh; ch++) {
                            var v = channelArrays[ch][i];
                            if (v > 1) v = 1; else if (v < -1) v = -1;
                            var off = (i * nCh + ch) * bytesPerSample;
                            if (bits === 24) {
                                // ×8388608 для обоих знаков — масштаб декодеров 24 бит
                                // (асимметричный ×8388607 у плюса: бит-в-бит 97 %).
                                var sm = Math.round(v * 8388608);
                                if (sm > 8388607) sm = 8388607; else if (sm < -8388608) sm = -8388608;
                                out[off] = sm & 0xFF; out[off + 1] = (sm >> 8) & 0xFF; out[off + 2] = (sm >> 16) & 0xFF;
                            } else {
                                odv.setFloat32(off, v, true);
                            }
                        }
                    }
                }
                // The sink copies what it is given; handing it the reused scratch
                // would let the next window overwrite bytes already queued.
                await sink.write(out.slice());
                dataBytes += need;
            },
            finalize: async function () {
                if (!headerWritten) { await sink.write(hdr); headerWritten = true; }
                var finalBlob = await sink.finish();
                var headBuf = await finalBlob.slice(0, headLen).arrayBuffer();
                var hbytes = new Uint8Array(headBuf);
                var hdv = new DataView(hbytes.buffer);
                hdv.setUint32(4, headLen - 8 + dataBytes, true);
                hdv.setUint32(headLen - 4, dataBytes, true);
                // slice() does not copy: the tail stays whatever the sink made it,
                // disk-backed included, so a 3 GB result never enters the heap.
                // The WAV the tool receives is this composite, so release() has
                // to know it by this Blob too.
                var wav = new Blob([hbytes, finalBlob.slice(headLen)], { type: 'audio/wav' });
                try { if (backing && backing.has(finalBlob)) backing.set(wav, backing.get(finalBlob)); } catch (_) {}
                return wav;
            },
            dataBytes: function () { return dataBytes; }
        };
    }

    /**
     * The sink's result with `head` in front — the MP3 encoder's Info frame (its encoder delay and
     * padding), known only once the last frame went by (MP3Encoder.encodeStream → finish().head).
     * The same composition wavWriter uses for its header: nothing is copied, a disk-backed result
     * stays on disk, and release() knows the composite by its own Blob too.
     */
    function prepend(blob, head) {
        if (!blob || !head || !head.length) return blob;
        var out = new Blob([head, blob], { type: blob.type || 'application/octet-stream' });
        try { if (backing && backing.has(blob)) backing.set(out, backing.get(blob)); } catch (_) {}
        return out;
    }

    window.OutputSink = {
        create: create,
        release: release,
        // The name a disk-backed audio result gets (stamp and owner first — the
        // janitor's `sink` kind). For a writer that keeps its own handle on the
        // file, like the screen recorder's tracks (SrOpfsStream.openWav).
        diskName: sinkFileName,
        prepend: prepend,
        isOpfsAvailable: isOpfsAvailable,
        wavWriter: wavWriter,
        wavHeader: wavHeader,
        WAV_MASK: WAV_MASK,
        WAV_MAX_DATA_BYTES: WAV_MAX_DATA_BYTES,
    };
})();
